import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

// Signs the user out after a period without any activity, so a session left open
// on a shared clinic computer does not keep patient data on screen.
// Activity in any tab counts for all tabs (shared via localStorage).
// Assessment answers are auto-saved, so timing out does not lose scoring work.
const IDLE_MINUTES = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES) || 30;
const WARN_BEFORE_MS = 2 * 60 * 1000;
const STORAGE_KEY = 'lastActivityAt';
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

const readLastActivity = () => {
  try {
    return Number(localStorage.getItem(STORAGE_KEY)) || Date.now();
  } catch (e) {
    return Date.now();
  }
};

const writeLastActivity = (t) => {
  try {
    localStorage.setItem(STORAGE_KEY, String(t));
  } catch (e) {
    /* storage unavailable: the in-memory value still works for this tab */
  }
};

export default function IdleLogout({ onTimeout }) {
  const idleMs = IDLE_MINUTES * 60 * 1000;
  const lastRef = useRef(Date.now());
  // Kept in a ref so a new onTimeout function never restarts the idle clock.
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const markActive = useCallback(() => {
    const now = Date.now();
    // Throttle writes: at most every 5 seconds.
    if (now - lastRef.current > 5000) {
      lastRef.current = now;
      writeLastActivity(now);
    }
  }, []);

  useEffect(() => {
    lastRef.current = Date.now();
    writeLastActivity(lastRef.current);
    EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const timer = setInterval(() => {
      const last = Math.max(lastRef.current, readLastActivity());
      const remaining = last + idleMs - Date.now();
      if (remaining <= 0) {
        clearInterval(timer);
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
        onTimeoutRef.current();
      } else if (remaining <= WARN_BEFORE_MS) {
        setWarning(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      } else {
        setWarning(false);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      EVENTS.forEach((e) => window.removeEventListener(e, markActive));
    };
  }, [idleMs, markActive]);

  const stay = () => {
    lastRef.current = Date.now();
    writeLastActivity(lastRef.current);
    setWarning(false);
  };

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, '0');

  return (
    <Dialog open={warning} onClose={stay} aria-labelledby="idle-logout-title">
      <DialogTitle id="idle-logout-title">Are you still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          For patient privacy you will be signed out in {minutes}:{seconds} because of inactivity.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onTimeout}>Sign out now</Button>
        <Button onClick={stay} variant="contained" autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
